import { useEffect, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import { getAllGyms } from "../../services/gymService";
import "./ExpiryMonitor.css";
import { FaPhoneAlt, FaWhatsapp } from "react-icons/fa";

export default function ExpiryMonitor() {
  const [gyms, setGyms] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await getAllGyms();
      setGyms(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysLeft = (expiryDate) => {
    if (!expiryDate) return 0;

    const today = new Date();
    const expiry = new Date(expiryDate);

    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const sortedGyms = [...gyms].sort((a, b) => {
    return (
      calculateDaysLeft(a.subscriptionEndDate) -
      calculateDaysLeft(b.subscriptionEndDate)
    );
  });

  const searchedGyms = sortedGyms.filter((gym) => {
    const keyword = search.toLowerCase();

    return (
      gym.gymName?.toLowerCase().includes(keyword) ||
      gym.ownerName?.toLowerCase().includes(keyword) ||
      gym.phoneNumber?.toLowerCase().includes(keyword)
    );
  });

  const filteredGyms = searchedGyms.filter((gym) => {
    const days = calculateDaysLeft(gym.subscriptionEndDate);

    switch (filter) {
      case "Active":
        return days > 7;

      case "Expiring":
        return days > 0 && days <= 7;

      case "Expired":
        return days <= 0;

      default:
        return true;
    }
  });

  const totalGyms = gyms.length;

  const activeGyms = gyms.filter(
    (gym) => calculateDaysLeft(gym.subscriptionEndDate) > 7,
  ).length;

  const expiringGyms = gyms.filter((gym) => {
    const days = calculateDaysLeft(gym.subscriptionEndDate);

    return days > 0 && days <= 7;
  }).length;

  const expiredGyms = gyms.filter(
    (gym) => calculateDaysLeft(gym.subscriptionEndDate) <= 0,
  ).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">Loading...</div>
      </AdminLayout>
    );
  }

  // const getWhatsAppNumber = (phone) => {
  //   if (!phone) return "";

  //   const cleaned = phone.replace(/\D/g, "");

  //   return cleaned.startsWith("91") ? cleaned : `91${cleaned}`;
  // };

  return (
    <AdminLayout>
      <div className="expiry-container">
        <div className="expiry-header">
          <div>
            <h1>Subscription Expiry Monitor</h1>

            <p>
              Monitor expiring subscriptions and notify gym owners before
              expiry.
            </p>
          </div>
        </div>

        <div className="expiry-toolbar">
          <input
            type="text"
            placeholder="Search Gym, Owner or Phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="expiry-search"
          />

          <div className="expiry-filters">
            {["All", "Active", "Expiring", "Expired"].map((item) => (
              <button
                key={item}
                onClick={() => setFilter(item)}
                className={filter === item ? "active-filter" : ""}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="expiry-stats">
          <div className="expiry-stat-card total">
            <h4>Total Gyms</h4>
            <h2>{totalGyms}</h2>
          </div>

          <div className="expiry-stat-card active">
            <h4>Active</h4>
            <h2>{activeGyms}</h2>
          </div>

          <div className="expiry-stat-card warning">
            <h4>Expiring Soon</h4>
            <h2>{expiringGyms}</h2>
          </div>

          <div className="expiry-stat-card danger">
            <h4>Expired</h4>
            <h2>{expiredGyms}</h2>
          </div>
        </div>
        {/* Add this below */}
        <div className="expiry-table-card">
          <table className="expiry-table">
            <thead>
              <tr>
                <th>Gym</th>

                <th>Owner</th>

                <th>Phone</th>

                <th>Plan</th>

                <th>Expiry Date</th>

                <th>Days Left</th>

                <th>Status</th>

                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredGyms.length > 0 ? (
                filteredGyms.map((gym) => {
                  const daysLeft = calculateDaysLeft(gym.subscriptionEndDate);

                  const phone = gym.phone?.replace(/\D/g, "");

                  const whatsappNumber = phone?.startsWith("91")
                    ? phone
                    : `91${phone}`;

                  const whatsappMessage = `Hello ${gym.ownerName},

Your ${gym.subscriptionPlan} (${gym.subscriptionDuration}) subscription for ${gym.gymName} ${
                    daysLeft <= 0
                      ? "has expired."
                      : `will expire in ${daysLeft} day${daysLeft > 1 ? "s" : ""}.`
                  }

Please renew your subscription to continue using NN AI Gym Management System.

Thank you,
NN AI Solutions`;

                  return (
                    <tr key={gym.id}>
                      <td>
                        <strong>{gym.gymName}</strong>
                      </td>

                      <td>{gym.ownerName}</td>

                      <td>{gym.phone}</td>

                      <td>
                        <div className="plan-info">
                          <span className="plan-pill">
                            {gym.subscriptionPlan}
                          </span>

                          <small>{gym.subscriptionDuration}</small>
                        </div>
                      </td>

                      <td>{formatDate(gym.subscriptionEndDate)}</td>

                      <td>{daysLeft <= 0 ? "Expired" : `${daysLeft} Days`}</td>

                      <td>
                        {daysLeft <= 0 ? (
                          <span className="status expired">Expired</span>
                        ) : daysLeft <= 7 ? (
                          <span className="status warning">Expiring Soon</span>
                        ) : (
                          <span className="status active">Active</span>
                        )}
                      </td>

                      <td>
                        <div className="action-buttons">
                          <a
                            href={`tel:${gym.phoneNumber}`}
                            className="call-btn"
                            title="Call"
                          >
                            <FaPhoneAlt size={16} />
                          </a>

                          <a
                            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                              whatsappMessage,
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            className="whatsapp-btn"
                            title="WhatsApp"
                          >
                            <FaWhatsapp size={18} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8">No Gyms Found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
